import { FileUIPart } from 'ai';
import { useRef, useState } from 'react';
import { useUploadRachelFile } from '../../../services/modules/rachelFiles/rachelFiles.mutations';
import { RachelFileValidationError, buildFileUIParts, validateRachelFile } from '../rachel.files';
import { RachelAttachment } from '../rachel.files.types';

interface UseRachelAttachmentsParams {
  onValidationError: (error: RachelFileValidationError, file: File) => void;
  onUploadError?: (error: unknown, file: File) => void;
}

export const useRachelAttachments = ({
  onValidationError,
  onUploadError,
}: UseRachelAttachmentsParams) => {
  const [attachments, setAttachments] = useState<RachelAttachment[]>([]);
  const attachmentsRef = useRef<RachelAttachment[]>([]);
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map());
  const uploadRachelFile = useUploadRachelFile();

  const applyAttachmentsUpdate = (update: (current: RachelAttachment[]) => RachelAttachment[]) => {
    attachmentsRef.current = update(attachmentsRef.current);
    setAttachments(attachmentsRef.current);
  };

  const updateAttachment = (id: string, update: Partial<RachelAttachment>) => {
    applyAttachmentsUpdate((current) =>
      current.map((attachment) =>
        attachment.id === id ? { ...attachment, ...update } : attachment,
      ),
    );
  };

  const addFiles = (files: File[]) => {
    for (const file of files) {
      const validationError = validateRachelFile(file);
      if (validationError) {
        onValidationError(validationError, file);
        continue;
      }

      const id = crypto.randomUUID();
      const controller = new AbortController();
      uploadControllersRef.current.set(id, controller);
      applyAttachmentsUpdate((current) => [...current, { id, file, status: 'uploading' }]);

      void uploadRachelFile
        .mutateAsync({ file, signal: controller.signal })
        .then((output) => updateAttachment(id, { status: 'ready', output }))
        .catch((error: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          updateAttachment(id, { status: 'error' });
          onUploadError?.(error, file);
        })
        .finally(() => {
          uploadControllersRef.current.delete(id);
        });
    }
  };

  const removeAttachment = (id: string) => {
    uploadControllersRef.current.get(id)?.abort();
    uploadControllersRef.current.delete(id);
    applyAttachmentsUpdate((current) => current.filter((attachment) => attachment.id !== id));
  };

  const consumeReadyParts = (attachmentIds?: string[]): FileUIPart[] => {
    const requestedIds = attachmentIds ? new Set(attachmentIds) : null;
    const readyAttachments = attachmentsRef.current.filter(
      (attachment) =>
        attachment.status === 'ready' && (!requestedIds || requestedIds.has(attachment.id)),
    );
    const consumedIds = new Set(readyAttachments.map((attachment) => attachment.id));
    const parts = buildFileUIParts(readyAttachments);
    applyAttachmentsUpdate((current) =>
      current.filter((attachment) => !consumedIds.has(attachment.id)),
    );
    return parts;
  };

  const hasBlockingAttachment = attachments.some(
    (attachment) => attachment.status === 'uploading' || attachment.status === 'error',
  );

  return { attachments, addFiles, removeAttachment, consumeReadyParts, hasBlockingAttachment };
};
